import { ActionIcon, Button, Flex, Stack, Text, TextInput } from '@mantine/core';
import { IconPlus, IconTrash } from '@tabler/icons-react';

export interface Pair {
  value: string;
  label: string;
}

interface Props {
  label: string;
  value: Pair[];
  onChange: (value: Pair[]) => void;
  maxItems?: number;
}

export default function PairsInput({ label, value, onChange, maxItems = 4 }: Props) {
  const update = (index: number, patch: Partial<Pair>) =>
    onChange(value.map((pair, i) => (i === index ? { ...pair, ...patch } : pair)));
  const itemName = label.toLowerCase().replace(/s$/, '');

  return (
    <Stack gap="xs">
      <Text size="sm" fw={500}>{label}</Text>
      {value.map((pair, index) => (
        <Flex key={index} gap="sm" align="center">
          <TextInput
            aria-label={`${label} ${index + 1} value`}
            placeholder="Value"
            w={120}
            value={pair.value}
            onChange={(event) => update(index, { value: event.currentTarget.value })}
          />
          <TextInput
            aria-label={`${label} ${index + 1} label`}
            placeholder="Label"
            flex={1}
            value={pair.label}
            onChange={(event) => update(index, { label: event.currentTarget.value })}
          />
          <ActionIcon
            variant="subtle"
            color="red"
            aria-label={`Remove ${itemName} ${index + 1}`}
            onClick={() => onChange(value.filter((_, i) => i !== index))}
          >
            <IconTrash size={16} />
          </ActionIcon>
        </Flex>
      ))}
      {value.length < maxItems && (
        <Button
          variant="light"
          size="xs"
          leftSection={<IconPlus size={14} />}
          style={{ alignSelf: 'flex-start' }}
          onClick={() => onChange([...value, { value: '', label: '' }])}
        >
          Add {itemName}
        </Button>
      )}
    </Stack>
  );
}

export function toPairs(value: unknown): Pair[] {
  if (!Array.isArray(value)) return [];
  return value
    .filter((item): item is Record<string, unknown> => typeof item === 'object' && item !== null)
    .map((item) => ({ value: String(item.value ?? ''), label: String(item.label ?? '') }));
}
